import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill,
    CopperButton, SlateButton, Modal, Field, TextInput, Select,
} from "@/components/common/UI";
import { useAuth, hasRole } from "@/lib/auth";
import { Cog, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import TID from "@/constants/testIds";
import { ImportExportBar } from "@/components/foundation/ImportExportBar";

const MACHINE_TYPES = [
    "LASER", "CNC_PRESS_BRAKE", "ROBOT_WELDER", "MIG_WELDER", "DRILL",
    "BANDSAW", "HYD_PRESS", "MECH_PRESS", "PAINT_LINE", "CED",
];
const UOMS = ["PER_HOUR", "PER_KG", "PER_BOX", "PER_PIECE", "LUMP", "PER_STROKE"];

const emptyForm = { code: "", operation_name: "", machine_type: "", default_uom: "PER_HOUR", description: "", is_active: true };

export default function ConversionOperations() {
    const { user } = useAuth();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);
    const canEdit = hasRole(user, "CORPORATE_ADMIN");

    const load = () =>
        api.get("/conversion-operations")
            .then((r) => setRows(r.data))
            .catch((e) => setError(formatApiError(e)));
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm);
        setModalOpen(true);
    };
    const openEdit = (row) => {
        setEditing(row);
        setForm({
            code: row.code, operation_name: row.operation_name,
            machine_type: row.machine_type || "", default_uom: row.default_uom,
            description: row.description || "", is_active: row.is_active,
        });
        setModalOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = { ...form, machine_type: form.machine_type || null };
            if (editing) await api.put(`/conversion-operations/${editing.id}`, payload);
            else await api.post("/conversion-operations", payload);
            toast.success(editing ? "Operation updated" : "Operation created");
            setModalOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete conversion operation "${row.operation_name}" (${row.code})? This action cannot be undone.`)) return;
        try {
            await api.delete(`/conversion-operations/${row.id}`);
            toast.success("Operation deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid={TID.conversionOpsPage}>
            <PageHeader
                eyebrow="FOUNDATION · LAYER 5B"
                title="Conversion Operations"
                subtitle="The master list of standard manufacturing operations (Forging, CNC Turning, Drilling…) that a ZBC's Conversion Cost picks from, instead of free text. Machine and Rate still come from the plant's own Machine Rates table."
                actions={
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        <ImportExportBar entity="conversion-operations" label="Conversion Operations" canImport={canEdit} onImported={load} />
                        {canEdit && (
                            <CopperButton onClick={openCreate} data-testid={TID.conversionOpsAddBtn}>
                                <Plus className="h-4 w-4" /> Add Operation
                            </CopperButton>
                        )}
                    </div>
                }
            />

            {error && <ErrorState message={error} onRetry={load} />}
            {!error && rows === null && <LoadingState />}

            {rows && rows.length === 0 && (
                <EmptyState
                    icon={Cog}
                    title="No conversion operations yet"
                    description="Add the operation types your plants commonly run (Forging, CNC Turning, Drilling, Milling…) so they show up as a dropdown on every ZBC."
                    action={canEdit && <CopperButton onClick={openCreate}><Plus className="h-4 w-4" /> Add First Operation</CopperButton>}
                />
            )}

            {rows && rows.length > 0 && (
                <Card>
                    <table data-testid={TID.conversionOpsTable} className="w-full table-rowhover">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50/60">
                                <th className="eyebrow text-left py-2 px-4">Code</th>
                                <th className="eyebrow text-left py-2 px-4">Operation Name</th>
                                <th className="eyebrow text-left py-2 px-4">Machine Type</th>
                                <th className="eyebrow text-left py-2 px-4">Default UOM</th>
                                <th className="eyebrow text-left py-2 px-4">Description</th>
                                <th className="eyebrow text-left py-2 px-4">Status</th>
                                <th className="eyebrow text-right py-2 px-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} data-testid={`conversion-op-row-${r.id}`} className="border-b border-slate-100">
                                    <td className="py-1.5 px-4 text-[13px] font-mono font-semibold text-slate-900">{r.code}</td>
                                    <td className="py-1.5 px-4 text-[13px]">{r.operation_name}</td>
                                    <td className="py-1.5 px-4 text-[12px] text-slate-600 font-mono">{r.machine_type || <span className="text-slate-400">Any</span>}</td>
                                    <td className="py-1.5 px-4 text-[12px] text-slate-600 font-mono">{r.default_uom}</td>
                                    <td className="py-1.5 px-4 text-[12px] text-slate-600">{r.description || "—"}</td>
                                    <td className="py-1.5 px-4">
                                        <Pill tone={r.is_active ? "emerald" : "slate"}>{r.is_active ? "Active" : "Inactive"}</Pill>
                                    </td>
                                    <td className="py-1.5 px-4 text-right">
                                        {canEdit && (
                                            <div className="inline-flex gap-1">
                                                <SlateButton onClick={() => openEdit(r)} className="h-7 px-2.5 text-xs">Edit</SlateButton>
                                                <SlateButton
                                                    onClick={() => remove(r)}
                                                    variant="ghost"
                                                    className="h-7 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                                                    title={`Delete ${r.code}`}
                                                    data-testid={`conversion-op-delete-${r.id}`}
                                                >
                                                    <Trash2 className="h-3 w-3" /> Delete
                                                </SlateButton>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}

            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                title={editing ? `Edit Operation — ${editing.code}` : "Add New Conversion Operation"}
                footer={
                    <>
                        <SlateButton variant="outline" onClick={() => setModalOpen(false)} data-testid={TID.modalCancel}>Cancel</SlateButton>
                        <CopperButton onClick={submit} disabled={submitting} data-testid={TID.modalSubmit}>
                            {submitting ? "Saving…" : (editing ? "Save Changes" : "Create Operation")}
                        </CopperButton>
                    </>
                }
            >
                <form onSubmit={submit} className="space-y-5">
                    <Field label="Code" required helper="e.g., OP-FORGE (uppercase, hyphenated)">
                        <TextInput
                            value={form.code}
                            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                            placeholder="OP-FORGE"
                            required
                            disabled={!!editing}
                        />
                    </Field>
                    <Field label="Operation Name" required helper="Shown as the Operation Name dropdown option on every ZBC">
                        <TextInput
                            value={form.operation_name}
                            onChange={(e) => setForm({ ...form, operation_name: e.target.value })}
                            placeholder="Forging"
                            required
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Machine Type" helper="Optional hint only; the ZBC's Machine dropdown still lists that plant's actual machines">
                            <Select value={form.machine_type} onChange={(e) => setForm({ ...form, machine_type: e.target.value })}>
                                <option value="">Any / unspecified</option>
                                {MACHINE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </Select>
                        </Field>
                        <Field label="Default UOM">
                            <Select value={form.default_uom} onChange={(e) => setForm({ ...form, default_uom: e.target.value })}>
                                {UOMS.map((u) => <option key={u} value={u}>{u}</option>)}
                            </Select>
                        </Field>
                    </div>
                    <Field label="Description">
                        <TextInput value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g., Hot forging on the mechanical press line" />
                    </Field>
                    <Field label="Status">
                        <Select value={form.is_active ? "active" : "inactive"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "active" })}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </Select>
                    </Field>
                </form>
            </Modal>
        </div>
    );
}
